import { RenderStatus } from '@/shared/constants/status';
import type { Render } from '@/types/render';
import { getProgressAnchor } from '@/shared/lib/progress';

const SLOWDOWN_THRESHOLD = 90;
const MAX_PREDICTED_PROGRESS = 95;

type RenderStatusType = (typeof RenderStatus)[keyof typeof RenderStatus];

/**
 * Check if render is in waiting state
 * PENDING = initial state when render is created but not yet started
 */
export function isRenderWaiting(status: RenderStatusType): boolean {
  return status === RenderStatus.PENDING;
}

/**
 * Check if render is actively processing
 */
export function isRenderProcessing(status: RenderStatusType): boolean {
  return status === RenderStatus.PROCESSING;
}

/**
 * Check if render is in queue (either waiting or actively processing)
 * Used to identify renders that are not yet finished
 */
export function isRenderInQueue(status: RenderStatusType): boolean {
  return isRenderWaiting(status) || isRenderProcessing(status);
}

/**
 * Check if render has completed successfully
 */
export function isRenderCompleted(status: RenderStatusType): boolean {
  return status === RenderStatus.COMPLETED;
}

/**
 * Check if render has failed
 */
export function isRenderFailed(status: RenderStatusType): boolean {
  return status === RenderStatus.FAILED;
}

/**
 * A render that ended without an image: failed, cancelled, or a status this
 * build does not know yet. Only `completed` counts as success; an unknown value
 * must never read as a finished render (Apple once mapped everything unknown,
 * `cancelled` included, to completed, so cancelled renders looked done).
 */
export function isRenderUnsuccessful(status: string): boolean {
  return (
    status !== RenderStatus.PENDING &&
    status !== RenderStatus.PROCESSING &&
    status !== RenderStatus.COMPLETED
  );
}

/**
 * Get render from object or return the render itself
 */
export function getRenderStatus(
  render: Render | { attributes: { status: RenderStatusType } }
): RenderStatusType {
  return render.attributes.status;
}

/**
 * Check if we should show progress UI for this render
 */
export function shouldShowRenderProgress(render: Render): boolean {
  const status = getRenderStatus(render);
  return isRenderInQueue(status);
}

/**
 * Calculate progress percentage based on time for renders without real progress updates
 * Returns value between 0 and 95 (never reaches 100 until actually complete)
 * Standardized across all components to use 95% cap
 *
 * durationPredicted comes from backend for each job individually.
 * Backend calculates it based on: user plan, job type, provider, system load.
 *
 * @param startedAt - ISO string or Date when job started (wall clock fallback)
 * @param durationPredicted - Predicted duration in seconds
 * @param jobId - Optional job ID for monotonic anchor lookup (immune to clock skew)
 */
export function calculateTimeBasedProgress(
  startedAt: string | Date | null,
  durationPredicted: number,
  jobId?: string
): number {
  // Try monotonic anchor first (immune to device-server clock skew)
  if (jobId) {
    const anchor = getProgressAnchor(jobId);
    if (anchor && anchor.durationPredicted > 0) {
      const elapsedSinceAnchorMs = performance.now() - anchor.anchorUptime;
      const elapsedSinceAnchorSec = elapsedSinceAnchorMs / 1000;
      const extrapolated =
        anchor.anchorProgress +
        (elapsedSinceAnchorSec / anchor.durationPredicted) * 100;
      return Math.min(MAX_PREDICTED_PROGRESS, Math.floor(extrapolated));
    }
  }

  // Fallback to wall clock
  if (!startedAt || !durationPredicted) return 0;

  const startTime =
    typeof startedAt === 'string'
      ? new Date(startedAt).getTime()
      : startedAt.getTime();
  const elapsedSeconds = (Date.now() - startTime) / 1000;

  // Calculate base progress
  let progress = (elapsedSeconds / durationPredicted) * 100;

  // Slow down after threshold
  if (progress >= SLOWDOWN_THRESHOLD) {
    // Logarithmic slowdown
    const beyondNormal =
      elapsedSeconds - durationPredicted * (SLOWDOWN_THRESHOLD / 100);
    const slowdownFactor = Math.log10(beyondNormal + 1) * 2;
    progress =
      SLOWDOWN_THRESHOLD +
      Math.min(MAX_PREDICTED_PROGRESS - SLOWDOWN_THRESHOLD, slowdownFactor);
  }

  // Standardized cap at max predicted progress until actually complete
  return Math.min(MAX_PREDICTED_PROGRESS, Math.floor(progress));
}
